import { ChipLabel } from "@rosc/ui/v32/chip";
import { Dialog } from "@rosc/ui/v32/sheet";
import { ChevronRight, FlaskConical, Mic, Play } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { practiceScript } from "@/lib/guided/practice";
import { cn } from "@/lib/utils";

// Guided first visit: the practice consulta. A "Practice" label, the choice
// between the sample recording and reading the script, and the script itself
// (on the upload screen, on New visit and during the recording).

export type PracticeMode = "sample" | "read";

/** The "Practice" label of a practice consulta (list, visit, recorder). */
export const PracticeBadge = ({
  className,
}: {
  className?: string;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <ChipLabel
      className={cn(
        "bg-v3-brand-tint text-v3-brand-strong shadow-none",
        className,
      )}
      data-testid="practice-badge"
    >
      <FlaskConical aria-hidden />
      {t("guided.practice.badge")}
    </ChipLabel>
  );
};

/** The practice conversation, line by line, with who says it. */
export const PracticeScript = ({
  language,
  read = false,
  compact = false,
  badge = true,
  className,
}: {
  language: string;
  /** The "Practice" label (off where the screen already shows it). */
  badge?: boolean;
  /** The doctor reads every line aloud (both voices). */
  read?: boolean;
  /** Recorder: a shorter box that scrolls. */
  compact?: boolean;
  className?: string;
}): JSX.Element => {
  const { t } = useTranslation();
  const { lines } = practiceScript(language);
  return (
    <section
      aria-label={t("guided.practice.scriptTitle")}
      className={cn(
        "rounded-[14px] bg-v3-surface-2 p-4 text-left font-v3 shadow-v3-hairline",
        className,
      )}
      data-testid="practice-script"
    >
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 text-[13px] font-semibold text-v3-text">
          {read
            ? t("guided.practice.readTitle")
            : t("guided.practice.scriptTitle")}
        </p>
        {badge && <PracticeBadge />}
      </div>
      {read && (
        <p className="mt-1 text-[12.5px] leading-snug text-v3-text-3">
          {t("guided.practice.readHint")}
        </p>
      )}
      <ol
        className={cn(
          "mt-3 space-y-2",
          compact && "max-h-[30dvh] overflow-y-auto pr-1",
        )}
      >
        {lines.map((line, index) => (
          <li
            // biome-ignore lint/suspicious/noArrayIndexKey: a fixed script.
            key={index}
            className="flex gap-2.5 text-[13.5px] leading-[1.45]"
          >
            <span
              className={cn(
                "mt-px w-[68px] shrink-0 text-[11.5px] font-semibold uppercase tracking-[0.04em]",
                line.speaker === "doctor"
                  ? "text-v3-brand-strong"
                  : "text-v3-text-3",
              )}
            >
              {line.speaker === "doctor"
                ? t("guided.practice.doctor")
                : t("guided.practice.patient")}
            </span>
            <span className="min-w-0 flex-1 text-v3-text">{line.text}</span>
          </li>
        ))}
      </ol>
    </section>
  );
};

const Choice = ({
  icon,
  title,
  hint,
  testId,
  onClick,
  disabled,
}: {
  icon: JSX.Element;
  title: string;
  hint: string;
  testId: string;
  onClick: () => void;
  disabled?: boolean;
}): JSX.Element => (
  <button
    className="flex min-h-[72px] w-full items-center gap-3 rounded-[14px] bg-v3-panel p-3.5 text-left shadow-v3-hairline-strong transition-colors hover:bg-v3-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v3-brand disabled:pointer-events-none disabled:opacity-50"
    data-testid={testId}
    disabled={disabled}
    type="button"
    onClick={onClick}
  >
    <span
      aria-hidden
      className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-v3-surface-2 text-v3-text-2 shadow-v3-hairline [&_svg]:size-5"
    >
      {icon}
    </span>
    <span className="min-w-0 flex-1">
      <span className="block text-[15px] font-semibold leading-5 text-v3-text">
        {title}
      </span>
      <span className="mt-0.5 block text-[13px] leading-snug text-v3-text-3">
        {hint}
      </span>
    </span>
    <ChevronRight aria-hidden className="size-4 shrink-0 text-v3-text-3" />
  </button>
);

/** "Try a practice visit": play the sample or read the script. */
export const PracticeVisitDialog = ({
  open,
  onOpenChange,
  onChoose,
  loading = false,
  error = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChoose: (mode: PracticeMode) => void;
  /** The sample recording is loading. */
  loading?: boolean;
  /** The sample recording could not be loaded. */
  error?: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <Dialog
      closeLabel={t("common.close")}
      description={t("guided.practice.description")}
      open={open}
      testId="practice-dialog"
      title={t("guided.practice.title")}
      onOpenChange={onOpenChange}
    >
      <div className="mt-2 space-y-2">
        <Choice
          disabled={loading}
          hint={t("guided.practice.sampleHint")}
          icon={<Play className="fill-current" />}
          testId="practice-choose-sample"
          title={t("guided.practice.sample")}
          onClick={() => onChoose("sample")}
        />
        <Choice
          disabled={loading}
          hint={t("guided.practice.readChoiceHint")}
          icon={<Mic />}
          testId="practice-choose-read"
          title={t("guided.practice.read")}
          onClick={() => onChoose("read")}
        />
      </div>
      {error && (
        <p
          className="mt-2 rounded-[12px] bg-v3-rec-tint px-3.5 py-3 text-[13.5px] font-medium text-v3-rec-text"
          role="alert"
        >
          {t("guided.practice.sampleError")}
        </p>
      )}
      <p className="mt-2 flex items-start gap-2 text-[12.5px] leading-snug text-v3-text-3">
        <PracticeBadge className="shrink-0" />
        <span className="pt-1">{t("guided.practice.syntheticNote")}</span>
      </p>
    </Dialog>
  );
};
